import { useUserRiskControllerGetUserRisk } from '@/api/generated/default/default';
import { ScreeningDtoStatus } from '@/api/generated/model';
import { findIcon } from '@/components/icons/icon-catch/icon-catch';
import { TypeOfCancer } from '@/components/icons/icon-catch/types';
import { RISK_LOWER_THRESHOLD, RISK_UPPER_THRESHOLD } from '@/constants/risks';
import useRiskFactorModal from '@/hooks/use-risk-factor-modal';
import { useGetCondition } from '@/queries/conditions.queries';
import { useInterventions } from '@/queries/interventions.queries';
import { useGetResources } from '@/queries/resources.queries';
import { useGetRiskAnalysisByCancerType, useGetRiskFactors } from '@/queries/risk.queries';
import { useGetTestingPlan } from '@/queries/testing-plan.queries';
import { formatCancerTypeNameWithCapitalization, getCancerRiskSuffix, sortRiskFactors } from '@/utils';
import { useDisclosure } from '@cmpsr/components';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Intervention } from '../../action-plan/types';

type CancerParams = {
  cancerId: TypeOfCancer;
};

const getInterventionRiskDetails = (intervention: Intervention, cancerId: string) => {
  const cancersAffected = intervention.riskImpact.filter(({ cancerType }) => cancerType !== 'overall') || [];
  const cancerRiskDetails = cancersAffected.find(({ cancerType }) => cancerType === cancerId);
  const otherCancers = cancersAffected?.length ? cancersAffected.length - 1 : 0;
  const cancerRiskSuffix = getCancerRiskSuffix(cancerRiskDetails?.cancerType as TypeOfCancer);

  return {
    ...intervention,
    cancerType: cancerRiskDetails?.cancerType as TypeOfCancer,
    impact: cancerRiskDetails?.impact,
    otherCancers,
    cancerRiskSuffix,
    affectedCancersCount: cancersAffected.length,
  };
};

export default function useSingleCancerViewController() {
  const { cancerId } = useParams<CancerParams>();
  const { data: conditionData } = useGetCondition(cancerId ?? '');
  const { name, about, avgAgeOnset, fiveYearSurvivalRate } = conditionData ?? {
    name: cancerId,
    about: [],
    avgAgeOnset: '',
    fiveYearSurvivalRate: '',
  };
  const formattedName = formatCancerTypeNameWithCapitalization(name ?? '');

  const {
    showModalDetailRiskFactor,
    onHideModalDetailRiskFactor,
    handleOpenModal,
    selectedRiskFactor,
    affectedCancerTypes,
    impactsByCancerType,
  } = useRiskFactorModal();

  /** Risk data */
  const { data: riskData } = useUserRiskControllerGetUserRisk();
  const currentRisk =
    cancerId === 'overall'
      ? riskData?.risk.overallRisk ?? 0
      : riskData?.risk.risks.find(({ cancer }) => cancer === cancerId)?.risk ?? 0;
  const potentialRisk =
    cancerId === 'overall'
      ? riskData?.potentialRisk ?? 0
      : riskData?.risk.risks.find(({ cancer }) => cancer === cancerId)?.potentialRisk ?? 0;
  const averageRisk =
    cancerId === 'overall'
      ? riskData?.averageRiskPercentage ?? 0
      : riskData?.risk.risks.find(({ cancer }) => cancer === cancerId)?.averageRisk ?? 0;

  /*** Risk Factors ***/
  const { data: riskFactorsData } = useGetRiskFactors();
  const riskFactors = riskFactorsData?.factorsPerCancer.find(({ cancerType }) => cancerType === cancerId);

  const factorsIncreasingRisk = !riskFactors
    ? []
    : riskFactors.riskFactors.filter((factor) => factor.impact > 0).sort(sortRiskFactors);
  const factorsDecreasingRisk = !riskFactors
    ? []
    : riskFactors.riskFactors.filter((factor) => factor.impact <= 0).sort(sortRiskFactors);

  const totalIncreasingFactors = factorsIncreasingRisk.length;
  const totalDecreasingFactors = factorsDecreasingRisk.length;

  const riskComparisonData = {
    factorsIncreasingRisk,
    factorsDecreasingRisk,
    totalIncreasingFactors,
    totalDecreasingFactors,
    onOpenModal: handleOpenModal,
  };

  /*** Interventions ***/
  const { data: interventionsData } = useInterventions();
  const { interventions } = interventionsData || {};
  const activeCancerInterventions = interventions?.filter((intervention) => {
    const cancerImpact = intervention.riskImpact?.find(({ cancerType }) => cancerType === cancerId);
    return (intervention.status === 'AVAILABLE' || intervention.status === 'IN_PROGRESS') && cancerImpact;
  });

  const interventionsWithRiskData = activeCancerInterventions
    ?.map((intervention) => {
      if (!cancerId) return null;
      return getInterventionRiskDetails(intervention, cancerId);
    })
    .filter((intervention) => intervention !== null);

  const recommendedInterventions = interventionsWithRiskData
    ?.sort((a, b) => (b?.impact || 0) - (a?.impact || 0))
    .slice(0, 3) as Intervention[];

  /*** Modal Intervention Details ***/
  const { isOpen: isDetailsModalOpen, onOpen: onOpenDetailsModal, onClose: onCloseDetailsModal } = useDisclosure();
  const [selectedIntervention, setSelectedIntervention] = useState<Intervention | null>(null);

  const handleOpenDetailsModal = (intervention: Intervention) => {
    setSelectedIntervention(intervention);
    onOpenDetailsModal();
  };

  /*** Testing Plan ***/
  const { data: testingPlanData } = useGetTestingPlan();
  const testingPlanItem = (testingPlanData?.recommendations ?? [])
    ?.filter(({ options }) => options.some((option) => option.status === ScreeningDtoStatus.surfaced))
    ?.find((testSuggestion) => testSuggestion.cancerId === cancerId);

  /*** Cancer Guide ***/
  const route = window.location.pathname;
  const { data: resourcesData } = useGetResources(encodeURIComponent(route));
  const resources = resourcesData ?? [];

  const cancerGuideItem = resources.find((resource) => resource.title.toLowerCase().includes('guide'));
  const cancerGuideUrl = cancerGuideItem?.url ?? 'https://www.catchbio.com/blog/what-is-cancer';

  /*** Icon ***/
  const icon = findIcon(cancerId ?? 'biomarkers');

  const { data: riskAnalysisData } = useGetRiskAnalysisByCancerType(cancerId as TypeOfCancer);

  const decadeRisk = String(riskAnalysisData?.tenYears.current) ?? '';

  const riskBadgeColor =
    currentRisk > averageRisk * RISK_UPPER_THRESHOLD
      ? 'var(--chakra-colors-alert-error-default)'
      : currentRisk < averageRisk * RISK_LOWER_THRESHOLD
      ? 'var(--chakra-colors-alert-success-default)'
      : 'var(--chakra-colors-alert-warning-default)';

  return {
    cancerId: cancerId as TypeOfCancer,
    icon,
    cancerName: formattedName,
    currentRisk,
    averageRisk,
    potentialRisk,
    about,
    decadeRisk,
    averageAgeOnset: avgAgeOnset,
    fiveYearSurvivalRate,
    showModalDetailRiskFactor,
    onHideModalDetailRiskFactor,
    selectedRiskFactor,
    affectedCancerTypes,
    impactsByCancerType,
    recommendedInterventions,
    handleOpenDetailsModal,
    selectedIntervention,
    isDetailsModalOpen,
    onCloseDetailsModal,
    testingPlanItem,
    cancerGuideUrl,
    riskComparisonData,
    riskBadgeColor,
    riskAnalysisData: riskAnalysisData?.risks ?? [],
  };
}
